import React from 'react';
import { useParams } from 'react-router-dom';

import {
  Button,
  Caption,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Grid,
  Heading,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';

import { Citation } from './CitationText';
import {
  getSourceTypeLabel,
  getSourceUrl,
  parseCitedTextData,
} from './citation';

interface CitationModalProps {
  citation: Citation;
  isOpen: boolean;
  onClose: () => void;
}

export const CitationModal: React.FC<CitationModalProps> = ({
  citation,
  isOpen,
  onClose,
}) => {
  const { tenantId, patientId } = useParams<{
    tenantId: string;
    patientId: string;
  }>();
  const jsonData = parseCitedTextData(citation.text);
  const sourceType = citation.source.sourceType;
  const sourceLabel = getSourceTypeLabel(sourceType);

  // Get link to the original source page/document if available
  const sourceUrl = getSourceUrl(citation, tenantId, patientId);

  const handleViewOriginal = () => {
    if (sourceUrl) {
      window.open(sourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="4xl" className="flex max-h-[90vh] flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <Heading size="2xl" weight="semibold">
              Citation Details - {sourceLabel}
            </Heading>
          </DialogTitle>
        </DialogHeader>

        <Stack gap={4} className="min-h-0 flex-1 overflow-y-auto">
          {/* Metadata */}
          <Stack gap={2}>
            <Grid columns={2} gap={4}>
              <Stack gap={2} direction="horizontal">
                <Caption inline size="3xs" weight="bold" color="supporting">
                  Source Type:{' '}
                </Caption>
                <Text inline>{sourceLabel}</Text>
              </Stack>
              {citation.source.sourceDocId && (
                <Stack gap={2} direction="horizontal">
                  <Caption inline size="3xs" weight="bold" color="supporting">
                    Document ID:{' '}
                  </Caption>
                  <Text inline>{citation.source.sourceDocId}</Text>
                </Stack>
              )}
            </Grid>

            {formatDate.toLocale(citation.source.sourceTimestamp) && (
              <Stack gap={2} direction="horizontal">
                <Caption inline size="3xs" weight="bold" color="supporting">
                  Created:{' '}
                </Caption>
                <Text inline>
                  {formatDate.toLocale(citation.source.sourceTimestamp)}
                </Text>
              </Stack>
            )}

            <Stack gap={2}>
              <Stack gap={2} direction="horizontal">
                <Caption inline size="3xs" weight="bold" color="supporting">
                  Position:{' '}
                </Caption>
                <Text inline>
                  Characters {citation.startIndex}-{citation.endIndex}
                </Text>
              </Stack>
              {citation.source.externalUrl && (
                <Stack gap={2} direction="horizontal">
                  <Caption inline size="3xs" weight="bold" color="supporting">
                    External URL:{' '}
                  </Caption>
                  <Text inline className="truncate">
                    {citation.source.externalUrl}
                  </Text>
                </Stack>
              )}
            </Stack>
          </Stack>

          {/* Content */}
          <Stack gap={3}>
            <Heading size="xl" weight="semibold">
              Original Source Record
            </Heading>

            {jsonData ? (
              <div className="w-full overflow-auto rounded-lg bg-bg-gray-subtle p-4 font-mono text-sm">
                <pre>{JSON.stringify(jsonData, null, 2)}</pre>
              </div>
            ) : (
              <div className="rounded-lg border bg-bg-gray-subtle p-4">
                <Text className="whitespace-pre-wrap">{citation.text}</Text>
              </div>
            )}
          </Stack>
        </Stack>

        <Stack
          className="shrink-0 border-t pt-4"
          gap={2}
          direction="horizontal"
          justify="between"
        >
          {sourceUrl ? (
            <Button onClick={handleViewOriginal}>
              <Icon name="ExternalLink" size="auto" />
              View Source
            </Button>
          ) : (
            <div />
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
